// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/SortableTableHead.tsx
//
//  Cabeçalho de coluna clicável (promovido de features/financeiro/components/
//  ColunaOrdenavel.tsx, que agora só reexporta este componente).
//
//  Só reporta campo e direção. Lista paginada no servidor → mande `ordenarPor`
//  e `direcao` para a API (campo precisa estar na whitelist do controller);
//  lista pequena já carregada → use com `useOrdenacaoLocal`.
// ════════════════════════════════════════════════════════════════════════════

import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { DirecaoOrdenacao } from "@/lib/consultas";

export interface SortableTableHeadProps<T extends string> {
  /** Campo enviado ao backend (precisa estar na whitelist do controller). */
  campo: T;
  /** Campo atualmente ordenado. */
  ordenarPor: T | string;
  direcao: DirecaoOrdenacao;
  /** Alterna: mesmo campo inverte a direção; campo novo entra na direção padrão. */
  onOrdenar: (campo: T) => void;
  children: React.ReactNode;
  className?: string;
  /** Alinha o conteúdo à direita (colunas de valor). */
  alinharDireita?: boolean;
}

export function SortableTableHead<T extends string>({
  campo, ordenarPor, direcao, onOrdenar, children, className, alinharDireita,
}: SortableTableHeadProps<T>) {
  const ativa = ordenarPor === campo;
  const rotulo = typeof children === "string" ? children : campo;

  return (
    <TableHead
      className={cn(alinharDireita && "text-right", className)}
      aria-sort={ativa ? (direcao === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onOrdenar(campo)}
        aria-label={`Ordenar por ${rotulo}`}
        title={ativa
          ? `Ordenado por ${rotulo} (${direcao === "asc" ? "crescente" : "decrescente"}) — clique para inverter`
          : `Ordenar por ${rotulo}`}
        className={cn(
          "inline-flex items-center gap-1 select-none transition-colors hover:text-foreground",
          alinharDireita && "flex-row-reverse",
          ativa ? "font-semibold text-foreground" : "text-muted-foreground",
        )}
      >
        {children}
        {!ativa
          ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" aria-hidden />
          : direcao === "asc"
            ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
            : <ArrowDown className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </TableHead>
  );
}
